/**
 * src/pages/Register.jsx
 * Registro de nuevo usuario con JWT.
 * Opcionalmente redirige a checkout tras el registro (tier en URL param).
 */
import { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { registerUser } from '../api/billing'
import { crearCheckout } from '../api/billing'

export default function Register() {
  const [params] = useSearchParams()
  const tier = params.get('tier')
  const [form, setForm] = useState({ email: '', nombre: '', password: '' })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const setJwt = useAuthStore(state => state.setJwt)
  const navigate = useNavigate()

  function set(field) { return e => setForm(f => ({ ...f, [field]: e.target.value })) }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.email || !form.nombre || !form.password) { setError('Completa todos los campos'); return }
    if (form.password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres'); return }
    setLoading(true); setError('')
    try {
      const data = await registerUser(form)
      setJwt(data.access_token, { email: data.email, nombre: data.nombre, rol: data.rol })
      if (tier) {
        const { url } = await crearCheckout(tier)
        window.location.href = url
      } else {
        navigate('/', { replace: true })
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear la cuenta')
    } finally { setLoading(false) }
  }

  const inputStyle = {
    width: '100%', background: 'var(--color-surface-2)',
    border: '1px solid var(--color-border)', borderRadius: '8px',
    padding: '10px 14px', fontSize: '14px', fontFamily: 'var(--font-ui)',
    color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box',
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ width: '100%', maxWidth: '400px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, fontSize: '18px', color: 'var(--color-text)' }}>CM Monitor</div>
          <p style={{ fontSize: '12px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginTop: '4px' }}>CREAR CUENTA</p>
          {tier && <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}>Plan {tier} · 14 días gratis</div>}
        </div>
        <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '12px', padding: '28px', boxShadow: 'var(--shadow-lg)' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: 'var(--color-text-2)', marginBottom: '5px', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}>NOMBRE</label>
              <input style={inputStyle} value={form.nombre} onChange={set('nombre')} placeholder="Tu nombre" disabled={loading}
                onFocus={e => e.target.style.borderColor = 'var(--color-accent)'} onBlur={e => e.target.style.borderColor = 'var(--color-border)'} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: 'var(--color-text-2)', marginBottom: '5px', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}>EMAIL</label>
              <input type="email" style={inputStyle} value={form.email} onChange={set('email')} placeholder="tu@empresa.cl" disabled={loading}
                onFocus={e => e.target.style.borderColor = 'var(--color-accent)'} onBlur={e => e.target.style.borderColor = 'var(--color-border)'} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 500, color: 'var(--color-text-2)', marginBottom: '5px', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}>CONTRASEÑA</label>
              <input type="password" style={inputStyle} value={form.password} onChange={set('password')} placeholder="Mínimo 8 caracteres" disabled={loading}
                onFocus={e => e.target.style.borderColor = 'var(--color-accent)'} onBlur={e => e.target.style.borderColor = 'var(--color-border)'} />
            </div>
            {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '10px 12px', fontSize: '12px', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>{error}</div>}
            <button type="submit" disabled={loading} style={{ width: '100%', background: loading ? 'var(--color-accent-dim)' : 'var(--color-accent)', color: loading ? 'var(--color-accent)' : '#fff', border: 'none', borderRadius: '8px', padding: '11px', fontSize: '14px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-ui)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              {loading ? <><span style={{ width: '14px', height: '14px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />Creando cuenta...</> : tier ? `Crear cuenta y pagar plan ${tier}` : 'Crear cuenta'}
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: 'var(--color-text-3)' }}>
            ¿Ya tienes cuenta? <Link to={tier ? `/acceder?tier=${tier}` : '/acceder'} style={{ color: 'var(--color-accent)', textDecoration: 'none' }}>Iniciar sesión</Link>
          </p>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
